import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {SelectItem, TreeNode} from 'primeng/api';
import {OverlayPanel} from "primeng/overlaypanel";

import {Account, AccountService, ChartOfAccountInfo, CommonTransformationService, CommonTreeService, LedgerAccount, LedgerAccountInfo} from "@app/shared";
import {GeneralLedgerDataService} from '@app/finance/general-ledger/general-ledger-data.service';
import {plainToClass} from 'class-transformer';
import {PrimeNgConfig} from "@app/shared/classes/prime-ng-config";
import {ModelOptionService} from "@app/solution-manager/system/model-options/model-options-data.service";
import {ModelMapping} from "@app/solution-manager/system/model-options/model-options";

@Component({
  selector: 'app-accounts-by-root-account',
  templateUrl: './accounts-by-root-account.component.html',
  styleUrls: ['./accounts-by-root-account.component.css'],
  providers: [ModelOptionService]
})
export class AccountsByRootAccountComponent implements OnInit, OnChanges {

  @Input() ledgerAccountId: number;                           // if provided, set the selected root, accountNode, and ledger account automatically
  @Input() selectOnlyLeafNode: boolean = true;                // allow only selecting leaf node in the sub-account selection menu
  @Input() showOnlyActiveAccounts: boolean = true;            // get only active accounts for display
  @Input() showOnlyFiscalYearLedgerAccounts: boolean = true;  // get only accounts for the current fiscal year
  @Input() showModelOptionAccounts: boolean = false;
  @Input() selectedOption: number;
  @Output() handler: EventEmitter<LedgerAccount> = new EventEmitter<LedgerAccount>();  //emits back to <add-journal-entry></add-journal-entry> when this.account is set

  PrimeNgConfig = PrimeNgConfig;

  rootAccountsDropdown: SelectItem<Account>[];                // list of all root accounts
  rootAccount: Account;                                       // selected root account from the dropdown menu
  accountNode: TreeNode<Account>;                             // selected sub-account
  legerAccount: LedgerAccount;                                // ledger account for the selected account node
  accountsTreeNodes: TreeNode[];                              // list of sub-accounts in tree node structure

  accountsInModel: number[];


  constructor(private dataService: AccountService, private generalLedgerDataService: GeneralLedgerDataService, private transform: CommonTransformationService,
              private treeService: CommonTreeService, private modelOptionService: ModelOptionService) {
  }

  ngOnInit() {

    this.dataService.getRootAccountAsDropdown(this.showOnlyActiveAccounts, this.showOnlyFiscalYearLedgerAccounts).subscribe(modelRes => {
      if(this.showModelOptionAccounts && this.selectedOption){
        this.modelOptionService.getModelOptionById(this.selectedOption).subscribe(res => {
          let modelMappings = res?.modelMappings;
          this.accountsInModel = modelMappings?.map(m => m.accountId);
          let allRootAccountsDropdown = <SelectItem<Account>[]>modelRes;
          let applicableDropDowns = allRootAccountsDropdown?.filter(m => this.accountsInModel?.includes(m?.value?.id));
          this.rootAccountsDropdown = applicableDropDowns;
        });
      } else {
        this.rootAccountsDropdown = <SelectItem<Account>[]>modelRes;
      }

    });
  }

  /**
   * if ledgerAccountId is provided, set the rootAccount and accountsTreeNodes matching the ledgerAccountId
   *  rootAccount must be set to the most parent of the ledgerAccount
   *  accountsTreeNodes should be the children of the rootAccount
   *  account should match one of the accounts in the accountsTreeNodes with the same ledgerAccountId
   */
  ngOnChanges(changes: SimpleChanges): void {

    if (this.ledgerAccountId != this.legerAccount?.id) {
      this.dataService.getRootAccountInfo(this.ledgerAccountId).subscribe(rootAccountInfo => {
        this.dataService.getLedgerAccount(this.ledgerAccountId).subscribe(res => {
          this.legerAccount = plainToClass(LedgerAccount, res);

          if (!this.rootAccountsDropdown) {
            this.dataService.getRootAccountAsDropdown(this.showOnlyActiveAccounts, this.showOnlyFiscalYearLedgerAccounts).subscribe(modelRes => {
              if(this.showModelOptionAccounts && this.selectedOption){
                this.modelOptionService.getModelOptionById(this.selectedOption).subscribe(res => {
                  let modelMappings = res?.modelMappings;
                  this.accountsInModel = modelMappings?.map(m => m.accountId);
                  let allRootAccountsDropdown = <SelectItem<Account>[]>modelRes;
                  let applicableDropDowns = allRootAccountsDropdown?.filter(m => this.accountsInModel?.includes(m?.value?.id));
                  this.rootAccountsDropdown = applicableDropDowns;
                });
              } else {
                this.rootAccountsDropdown = <SelectItem<Account>[]>modelRes;
              }
              this.setRooAccountAndAccountsTreeNodes(rootAccountInfo);
            });
          } else {
            this.setRooAccountAndAccountsTreeNodes(rootAccountInfo);
          }
        });
      });
    }
  }

  /**
   * when root account selected from dropdown, based on selection it retrieves its child/sub accounts from backend and sets accountsTreeNodes
   */
  onSelectRootAccount(rootAccount: Account): void {
    this.accountNode = null;
    this.legerAccount = null;

    // FixMe jC: rel-1.3 sso-9548 this needs to return only active root accounts
    this.dataService.getAccountById(rootAccount.id, this.showOnlyFiscalYearLedgerAccounts).subscribe(res => {
      const selectedAccount = plainToClass(Account, res as Account);
      this.treeService.sortNodesAndChildren(selectedAccount.children, "accountNumber");
      this.accountsTreeNodes = this.transform.convertToLongLabelTree(["accountNumber", "accountName"], selectedAccount.children);

      if (this.accountsTreeNodes == null || this.accountsTreeNodes.length <= 0) {
        this.onSelectAccountNode(selectedAccount, false);
      } else if (this.selectOnlyLeafNode) {
        this.treeService.disableParentNodeSelection(this.accountsTreeNodes);
      }
    });
  }

  private setRooAccountAndAccountsTreeNodes(rootAccountInfo: ChartOfAccountInfo) {
    let index = this.rootAccountsDropdown.findIndex(acc => acc.value.id == rootAccountInfo.id);
    if (index >= 0) {
      this.rootAccount = this.rootAccountsDropdown[index].value;

      this.dataService.getAccountById(this.rootAccount.id, this.showOnlyFiscalYearLedgerAccounts).subscribe(res => {
        const selectedAccount = plainToClass(Account, res);
        this.treeService.sortNodesAndChildren(selectedAccount.children, "accountNumber");
        this.accountsTreeNodes = this.transform.convertToLongLabelTree(["accountNumber", "accountName"], selectedAccount.children);

        if (this.selectOnlyLeafNode) {
          this.treeService.disableParentNodeSelection(this.accountsTreeNodes);
        }

        this.accountNode = this.findAccount(this.accountsTreeNodes, this.legerAccount.accountId);
      });
    }
  }

  // ToDo jC: rel-1.3 nivas to find a better way than getting the entire ledger accounts
  /**
   * find and set ledger account associated with the selected account
   */
  onSelectAccountNode(account: Account, emit: boolean, op?: OverlayPanel): void {
    op?.hide();
    this.dataService.searchLedgerAccountInfo(null, null, null, account.id, false, this.showOnlyFiscalYearLedgerAccounts, true).subscribe(res => {
      const ledgerAccounts = plainToClass(LedgerAccountInfo, res.list);
      this.legerAccount = this.dataService.convertToLedgerAccount(ledgerAccounts[0]);
      if (emit) {
        this.handler.emit(this.legerAccount);
      }
    });
  }

  /**
   * find account matching the id recursively
   */
  private findAccount(accountTreeNodes: TreeNode<Account>[], accountId): Account {
    for (let node of accountTreeNodes) {
      if (node.data.id == accountId) {
        return node.data;
      }
      if (node.children.length > 0) {
        this.findAccount(node.children, accountId);
      }
    }
    return null;
  }
}
